






















































import {Inject} from 'vue-typedi';
import {StoreService} from '@/services/StoreService';
import formatDate from '@/filters/DateFormatter';
import {Component, Prop, Vue} from "vue-property-decorator";
import ZonePicto from '@/components/ZonePicto.vue';
import {SlotOrder} from "@/model/collection/SlotOrder";
import {PreparationStatus} from "@/model/collection/PreparationStatus";
import {CollectionStatus} from "@/model/collection/CollectionStatus";
import {Zone} from '@/model/Zone';
import ZoneFilter, {ZoneFilterBehaviour} from '@/components/ZoneFilter.vue';
import {EventBus} from '@/helpers/EventBus';
import {Slot} from "@/model/collection/Slot";
import {PlaybackService, Sounds} from '@/services/PlaybackService';
import SupportPanel from '@/components/supports/SupportPanel.vue';
import {CollectionErrorOptions, CollectionErrorContexts} from "@/components/collection-slot/CollectionSlotBody.vue"
import {Support, SUPPORT_STATUS_ORDER, SupportStatuses} from '@/model/support/Support';
import {SupportService} from "@/services/SupportService";
import { SupportDisplayContexts, SupportDisplayOptions } from '@/model/support/SupportDisplayOptions';
import ScanDetector from "@/helpers/ScanDetector";

@Component({
  components: {ZonePicto, ZoneFilter, SupportPanel},
  filters: {formatDate}
})
export default class CollectionSlotOrdersList extends Vue {

  @Inject()
  private storeService!: StoreService;
  @Inject()
  private playbackService!: PlaybackService;
  @Inject()
  private supportService!: SupportService;

  @Prop({default: null})
  protected selectedSlot: Slot;
  @Prop({default: []})
  private supportsToCollect: Support[];
  @Prop({default: []})
  private allCreatedSupports: Support[];
  @Prop({default: false})
  private canHandleSupports: boolean;

  // Expose enums to the vue
  private PreparationStatus = PreparationStatus;
  private CollectionStatus = CollectionStatus;
  private ZoneFilterBehaviour = ZoneFilterBehaviour;

  protected allZones: Zone[] = [];
  protected selectedZones: Zone[] = [];

  currentSupportId: string = null;
  isProcessingScan: boolean = false;
  scanDetector: ScanDetector = new ScanDetector((value: string) => {
    this.currentSupportId = value;
    if (this.canLoadSupport) {
      this.scanSupport(this.currentSupportId);
    }
  });

  created() {
    EventBus.$on(EventBus.REFRESH_STORE, this.refreshZones);
    this.storeService.refreshStore();
    this.focusInput();
  }

  beforeDestroy() {
    this.scanDetector.reset();
  }

  focusInput() {
    this.$nextTick(() => {
      (this.$refs.supportInput as HTMLInputElement)?.focus();
    });
  }

  onScanInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value;
    if (value == null) {
      return;
    }
    this.scanDetector.onInput(value);
  }

  refreshZones() {
    this.allZones = this.storeService.allZones;
  }

  isActive(zoneCode: string, order: SlotOrder): boolean {
    if (!order) {
      return false;
    }

    const involvedZones = order.involvedZones;
    return involvedZones && involvedZones.has(zoneCode);
  }

  scanSupport(supportCode: string): void {
    if (this.isProcessingScan) {
      return;
    }

    supportCode = supportCode?.trim();
    const support = this.supportsToCollect.find(s => s.code === supportCode);

    if (support == null) {
      if (this.isSupportInOtherSlot(supportCode)) {
        this.scanFail(
          CollectionErrorContexts.SUPPORT_FROM_OTHER_SLOT,
          supportCode
        );
        return;
      }

      this.scanFail(
        CollectionErrorContexts.SUPPORT_NOT_FOUND,
        supportCode
      );
      return;
    }

    if (this.isSupportAlreadyCollected(support)) {
      this.scanFail(
        CollectionErrorContexts.SUPPORT_ALREADY_COLLECTED,
        supportCode
      );
      return;
    }

    this.isProcessingScan = true;
    this.onCollectSupport(support);
    this.playbackService.play(Sounds.SCAN);
    this.finalizeScan();
  }


  scanFail(context: CollectionErrorContexts, code: string): void {
    this.handleScanError(context, code);
    this.finalizeScan();
  }


  isSupportAlreadyCollected(support: Support): boolean {
    return support?.supportStatus === SupportStatuses.COLLECTED;
  }

  handleScanError(
    context: CollectionErrorContexts,
    supportCode: string
  ): void {
    this.playbackService.play(Sounds.ERROR);

    this.$emit('scanerror', {
      context,
      supportCode
    } as CollectionErrorOptions);
  }

  finalizeScan(): void {
    this.currentSupportId = null;
    this.scanDetector.reset();
    this.focusInput();
  }

  isSupportInCurrentCollectionSlot(supportCode: string): boolean {
    return this.supportsToCollect.find(support => support.code === supportCode) != null;
  }

  isSupportInOtherSlot(supportCode: string): boolean {
    return this.allCreatedSupports
      .filter(() => !this.isSupportInCurrentCollectionSlot(supportCode))
      .find(support => support.code === supportCode) != null;
  }

  onCollectSupport(support: Support): void {
    this.supportService.collectSupport(support.code, this.selectedSlot.dueDate)
      .then(() => {
        support.supportStatus = SupportStatuses.COLLECTED;
        this.playbackService.play(Sounds.SCAN);
      }).catch(() => {
      const errorOptions: CollectionErrorOptions = {
        context: CollectionErrorContexts.COLLECTION_FAILED,
        supportCode: support.code
      };
      this.playbackService.play(Sounds.ERROR);
      this.$emit('scanerror',errorOptions);
    }).finally(() => {
      this.isProcessingScan = false;
      this.focusInput();
    });
  }

  loadSelectedZones(selectableZones: Zone[]) {
    const selectedZoneCodes = this.storeService.selectedAndEnabledZones;
    const selectedZones = selectableZones.filter(zone => selectedZoneCodes.includes(zone.zoneCode));
    if (selectedZones.length === 1) {
      this.selectedZones = selectedZones;
      return;
    }

    this.selectedZones = [];
  }

  get sortedSupports() {
    return [...this.supportsToCollect].sort((a, b) =>
      SUPPORT_STATUS_ORDER[a.supportStatus] - SUPPORT_STATUS_ORDER[b.supportStatus]
    );
  }

  onSelectionChanged(selectedZoneCodes: string[]) {
    this.selectedZones = this.selectableZones.filter(zone => selectedZoneCodes.includes(zone.zoneCode));
    this.storeService.saveSelectedZones(selectedZoneCodes);
  }

  get selectableZones(): Zone[] {
    const selectableZones = this.allZones.filter(zone => this.supportsToCollect.some(support => support.zoneCode === zone.zoneCode));
    this.loadSelectedZones(selectableZones);

    return selectableZones;
  }

  get totalSupportsCount(): number {
    return this.supportsToCollect.length;
  }

  get filteredSupportsCount(): number {
    return this.filteredSupports.length;
  }

  get collectedSupportsCount(): number {
    return this.supportsToCollect
      .filter(support => support.supportStatus === SupportStatuses.COLLECTED)
      .reduce((accumulator, currentValue) => accumulator + 1, 0);
  }

  get collectedFilteredSupportsCount(): number {
    return this.filteredSupports
      .filter(support => support.supportStatus === SupportStatuses.COLLECTED)
      .reduce((accumulator, currentValue) => accumulator + 1, 0);
  }

  get filteredSupports(): Support[] {
    const selectedZoneCodes = this.selectedZones.map(zone => zone.zoneCode);

    return this.sortedSupports.filter(support => selectedZoneCodes.length === 0 || selectedZoneCodes.includes(support.zoneCode));
  }

  get isFilterActive(): boolean {
    return this.selectedZones.length !== 0 && this.selectedZones.length !== this.selectableZones.length;
  }

  get extraLabelsByZone(): Map<string, string> {
    const map = new Map<string, string>();
    this.allZones.forEach(zone => {
      const supportsCount = this.supportsToCollect.filter(support => support.zoneCode === zone.zoneCode).length;

      if (!this.canHandleSupports) {
        if (supportsCount > 1) {
          map.set(zone.zoneCode, `(${supportsCount} commandes)`);
        } else {
          map.set(zone.zoneCode, `(${supportsCount} commande)`);
        }

        return;
      }

      if (supportsCount > 1) {
        map.set(zone.zoneCode, `(${supportsCount} supports)`);
      } else {
        map.set(zone.zoneCode, `(${supportsCount} support)`);
      }
    });

    return map;
  }

  get canLoadSupport(): boolean {
    return this.hasValidCurrentSupportId && !this.isProcessingScan;
  }

  get hasValidCurrentSupportId(): boolean {
    return this.currentSupportId != null && this.currentSupportId.trim() !== '';
  }

  get supportDisplayOptions(): SupportDisplayOptions {
    return {
      context: SupportDisplayContexts.COLLECTION,
      requiresManualAction: !this.canHandleSupports
    };
  }

  back() {
    this.$emit('back');
  }

  open(order: SlotOrder) {
    this.$emit('selectOrder', order);
  }
}
